import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Button,
  TextField,
  Divider,
  DialogActions
} from '@material-ui/core';

import AddAPhotoIcon from '@material-ui/icons/AddAPhoto';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import CameraIcon from '@material-ui/icons/Camera';
import ViewCarouselIcon from '@material-ui/icons/ViewCarousel';

import Webcam from 'react-webcam';

import { useFaceRegistration } from './useFaceRegistration';

const FaceRegistration = () => {
  const {
    webcamRef,
    canvasRef,
    handleRegisterFace,
    registeredFaces,
    handleSaveImages,
    bioID,
    setBioID,
    data,
    imgCount,
    capturePositions
  } = useFaceRegistration();

  const [openDialog, setOpenDialog] = useState(false);
  const [viewImages, setViewImages] = useState(false);
  const [selectedImg, setSelectedImg] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  const handleOpenPreview = index => {
    setPreviewIndex(index);
    setOpenPreview(true);
  };

  const handleNext = () => {
    setPreviewIndex(prev => Math.min(prev + 1, registeredFaces.length - 1));
  };

  const handlePrev = () => {
    setPreviewIndex(prev => Math.max(prev - 1, 0));
  };

  return (
    <Box width="100%">
      {/* MAIN BOX */}
      <Box
        style={{
          border: '1px solid #e0e0e0',
          borderRadius: 12,
          padding: 16,
          width: '100%',
          backgroundColor: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          boxSizing: 'border-box'
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          style={{
            marginBottom: 16,
            paddingBottom: 12,
            borderBottom: '1px solid #eeeeee'
          }}
        >
          <Box>
            <Box
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: '#333'
              }}
            >
              Face Images
            </Box>

            <Box
              style={{
                fontSize: 12,
                color: '#888',
                marginTop: 3
              }}
            >
              Registered facial images for recognition
            </Box>
          </Box>

          {data?.value?.images?.length > 0 && (
            <Box
              style={{
                background: '#e3f2fd',
                color: '#1976d2',
                padding: '5px 10px',
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 600
              }}
            >
              {data.value.images.length}{' '}
              {data.value.images.length === 1 ? 'Image' : 'Images'}
            </Box>
          )}
        </Box>

        {/* Images */}
        {data?.value?.images?.length > 0 ? (
          <Box
            display="flex"
            flexWrap="wrap"
            style={{
              gap: 12
            }}
          >
            {data.value.images.map((img, i) => (
              <Box
                key={i}
                onDoubleClick={() => {
                  setSelectedImg(img);
                  setViewImages(true);
                }}
                style={{
                  position: 'relative',
                  width: 100,
                  height: 100,
                  padding: 4,
                  borderRadius: 10,
                  background: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxSizing: 'border-box'
                }}
              >
                <img
                  src={img}
                  alt={`face-${i}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 7,
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />

                {/* Image number */}
                <Box
                  style={{
                    position: 'absolute',
                    bottom: 7,
                    left: 7,
                    background: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    fontSize: 10,
                    padding: '2px 6px',
                    borderRadius: 10
                  }}
                >
                  #{i + 1}
                </Box>

                {/* View button */}
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedImg(img);
                    setViewImages(true);
                  }}
                  style={{
                    position: 'absolute',
                    top: 5,
                    right: 5,
                    width: 28,
                    height: 28,
                    background: 'rgba(255,255,255,0.9)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  <AspectRatioIcon fontSize="small" color="primary" />
                </IconButton>
              </Box>
            ))}

            {/* Add another image */}
            <Box
              onClick={() => setOpenDialog(true)}
              style={{
                width: 100,
                height: 100,
                borderRadius: 10,
                border: '2px dashed #bdbdbd',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#777',
                transition: 'all 0.2s ease',
                boxSizing: 'border-box'
              }}
            >
              <AddAPhotoIcon fontSize="small" color="primary" />

              <Box
                style={{
                  fontSize: 11,
                  marginTop: 6
                }}
              >
                Update images
              </Box>
            </Box>
          </Box>
        ) : (
          /* Empty state */
          <Box
            onClick={() => setOpenDialog(true)}
            style={{
              minHeight: 180,
              border: '2px dashed #d0d0d0',
              borderRadius: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              background: '#fafafa',
              transition: 'all 0.2s ease'
            }}
          >
            <Box
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: '#e3f2fd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 10
              }}
            >
              <AddAPhotoIcon fontSize="medium" color="primary" />
            </Box>

            <Box
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: '#555'
              }}
            >
              No Face Images
            </Box>

            <Box
              style={{
                fontSize: 12,
                color: '#999',
                marginTop: 4
              }}
            >
              Click here to register a face image
            </Box>
          </Box>
        )}

        {/* Footer hint */}
        {data?.value?.images?.length > 0 && (
          <Box
            style={{
              marginTop: 12,
              fontSize: 11,
              color: '#999'
            }}
          >
            Double-click an image or click the expand icon to view it.
          </Box>
        )}
      </Box>

      {/* VIEW IMAGE DIALOG */}
      <Dialog
        open={viewImages}
        onClose={() => setViewImages(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 20
          }}
        >
          {selectedImg && (
            <img
              src={selectedImg}
              alt="preview"
              style={{
                maxWidth: '100%',
                maxHeight: 500,
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: 10
              }}
            />
          )}
        </DialogContent>

        <Divider />

        <DialogActions>
          <Button onClick={() => setViewImages(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* CAMERA DIALOG */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent style={{ padding: 0 }}>
          {/* CAMERA */}
          <Box
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '4/3',
              overflow: 'hidden',
              background: '#000',
              borderRadius: 12
            }}
          >
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/png"
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'scaleX(-1)'
              }}
            />

            <canvas
              ref={canvasRef}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                transform: 'scaleX(-1)'
              }}
            />

            {/* Capture Position */}
            <Box
              style={{
                position: 'absolute',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(0,0,0,0.7)',
                color: '#fff',
                padding: '8px 20px',
                borderRadius: 20,
                textAlign: 'center',
                zIndex: 10,
                minWidth: 150
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  opacity: 0.8
                }}
              >
                Capture {Math.min(registeredFaces.length + 1, imgCount)} of{' '}
                {imgCount}
              </div>

              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600
                }}
              >
                {capturePositions[registeredFaces.length] || 'Complete'}
              </div>
            </Box>

            {/* Progress */}
            <Box
              style={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 8,
                zIndex: 10
              }}
            >
              {capturePositions.slice(0, imgCount).map((position, index) => (
                <Box
                  key={position}
                  title={position}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',

                    background:
                      index < registeredFaces.length
                        ? '#4caf50'
                        : index === registeredFaces.length
                        ? '#2196f3'
                        : 'rgba(255,255,255,0.35)',

                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,

                    border:
                      index === registeredFaces.length
                        ? '2px solid #fff'
                        : 'none'
                  }}
                >
                  {index + 1}
                </Box>
              ))}
            </Box>
          </Box>

          {/* CONTROLS */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mt={2}
            style={{ padding: '0 10px' }}
          >
            {/* PREVIEW THUMB */}
            <Box
              onClick={() =>
                registeredFaces.length &&
                handleOpenPreview(registeredFaces.length - 1)
              }
              style={{
                width: 60,
                height: 60,
                background: '#f5f5f5',
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                overflow: 'hidden'
              }}
            >
              {registeredFaces?.length > 0 ? (
                <img
                  src={registeredFaces[registeredFaces.length - 1]}
                  alt="latest"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <ViewCarouselIcon style={{ opacity: 0.4 }} />
              )}
            </Box>

            {/* CAPTURE BUTTON */}
            <Button
              onClick={handleRegisterFace}
              variant="outlined"
              color="primary"
              style={{
                borderRadius: '50%',
                minWidth: 70,
                width: 70,
                height: 70
              }}
            >
              <CameraIcon style={{ fontSize: 50 }} />
            </Button>

            {/* BIO ID */}
            <Box style={{ width: 140 }} marginLeft={-4}>
              <TextField
                type="number"
                value={bioID}
                label="Bio ID"
                size="small"
                variant="outlined"
                fullWidth
                error={!bioID}
                helperText={!bioID ? 'Required' : ''}
                onChange={e => setBioID(e.target.value)}
              />
            </Box>
          </Box>

          {/* SAVE BUTTON */}
          <Button
            fullWidth
            size="large"
            variant="contained"
            color="primary"
            onClick={() => {
              handleSaveImages();
              setOpenDialog(false);
            }}
            style={{ marginTop: 10 }}
            disabled={registeredFaces.length !== Number(imgCount) || !bioID}
          >
            Proceed ({registeredFaces.length}/{imgCount})
          </Button>
        </DialogContent>
      </Dialog>

      {/* PREVIEW DIALOG */}
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20
          }}
        >
          {registeredFaces?.length > 0 && (
            <>
              <img
                src={registeredFaces[previewIndex]}
                alt={`preview-${previewIndex}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: 500,
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 10
                }}
              />

              <Box
                display="flex"
                justifyContent="space-between"
                width="100%"
                mt={2}
              >
                <Button
                  onClick={handlePrev}
                  disabled={previewIndex === 0}
                  color="primary"
                >
                  Prev
                </Button>

                <Button onClick={() => setOpenPreview(false)}>Close</Button>

                <Button
                  onClick={handleNext}
                  color="primary"
                  disabled={previewIndex === registeredFaces.length - 1}
                >
                  Next
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FaceRegistration;
